/**
 * Conflict List Widget
 * Shows planning conflicts and warnings with resolution controls
 */

import { useState, useMemo } from 'react';
import {
  AlertTriangle,
  AlertCircle,
  Info,
  Check,
  Filter,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Clock,
  Gauge,
  PackageX,
  Users,
  ArrowRightLeft,
  UserX,
  Wrench,
  X,
  Calendar,
} from 'lucide-react';
import clsx from 'clsx';
import type {
  PlanningConflict,
  ConflictSeverity,
  ConflictType,
  ConflictSummary,
} from '../../hooks/useConflicts';
import {
  getSeverityColor,
  calculateConflictSummary,
  sortConflictsByPriority,
} from '../../hooks/useConflicts';

interface ConflictListWidgetProps {
  conflicts?: PlanningConflict[];
  loading?: boolean;
  error?: Error | null;
  onRefresh?: () => void;
  onResolve?: (conflictId: string, note: string) => Promise<void>;
  onSelectScenario?: () => void;
  scenarioId?: string;
  scenarioName?: string;
  isResolving?: boolean;
}

// Icon component mapping
const ConflictTypeIcons: Record<ConflictType, typeof AlertTriangle> = {
  capacity_overload: Gauge,
  late_delivery: Clock,
  material_shortage: PackageX,
  resource_conflict: Users,
  sequence_violation: ArrowRightLeft,
  employee_unavailable: UserX,
  maintenance_conflict: Wrench,
};

const SeverityIcons: Record<ConflictSeverity, typeof AlertTriangle> = {
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

export function ConflictListWidget({
  conflicts = [],
  loading,
  error,
  onRefresh,
  onResolve,
  onSelectScenario,
  scenarioId,
  scenarioName,
  isResolving,
}: ConflictListWidgetProps) {
  const [showResolved, setShowResolved] = useState(false);
  const [selectedSeverity, setSelectedSeverity] = useState<ConflictSeverity | 'all'>('all');
  const [selectedType, setSelectedType] = useState<ConflictType | 'all'>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [resolutionNote, setResolutionNote] = useState('');

  // Calculate summary
  const summary = useMemo(() => calculateConflictSummary(conflicts), [conflicts]);

  // Filter and sort conflicts
  const filteredConflicts = useMemo(() => {
    let filtered = conflicts;

    // Filter by resolved status
    if (!showResolved) {
      filtered = filtered.filter((c) => !c.is_resolved);
    }

    // Filter by severity
    if (selectedSeverity !== 'all') {
      filtered = filtered.filter((c) => c.severity === selectedSeverity);
    }

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter((c) => c.conflict_type === selectedType);
    }

    return sortConflictsByPriority(filtered);
  }, [conflicts, showResolved, selectedSeverity, selectedType]);

  // Handle resolve
  const handleResolve = async (conflictId: string) => {
    if (!onResolve || !resolutionNote.trim()) return;
    await onResolve(conflictId, resolutionNote);
    setExpandedId(null);
    setResolutionNote('');
  };

  // No scenario selected
  if (!scenarioId) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-gray-500">
        <AlertTriangle className="w-12 h-12 mb-4 text-gray-300" />
        <p className="text-sm mb-3">No Scenario Selected</p>
        {onSelectScenario && (
          <button
            onClick={onSelectScenario}
            className="px-4 py-2 text-sm bg-[#45bdd6] text-white rounded hover:bg-[#3aa8c0] transition-colors"
          >
            Select Scenario
          </button>
        )}
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-[#45bdd6] border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500">Loading Conflicts...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-red-500">
        <AlertCircle className="w-10 h-10 mb-3" />
        <p className="text-sm text-center mb-3">{error.message || 'Error Loading'}</p>
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="px-4 py-2 text-sm border border-red-300 rounded hover:bg-red-50"
          >
            Try Again
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header with summary */}
      <div className="px-4 py-3 bg-gray-50 border-b">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-medium text-gray-800">
              {scenarioName || 'Conflicts'}
            </h3>
            <p className="text-xs text-gray-500">
              {summary.unresolved} open of {summary.total} conflicts
            </p>
          </div>
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="p-1.5 text-gray-500 hover:text-gray-700"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Summary badges */}
        <div className="flex gap-2 flex-wrap">
          <SummaryBadge
            count={summary.bySeverity.error}
            label="Errors"
            severity="error"
            selected={selectedSeverity === 'error'}
            onClick={() => setSelectedSeverity(selectedSeverity === 'error' ? 'all' : 'error')}
          />
          <SummaryBadge
            count={summary.bySeverity.warning}
            label="Warnings"
            severity="warning"
            selected={selectedSeverity === 'warning'}
            onClick={() => setSelectedSeverity(selectedSeverity === 'warning' ? 'all' : 'warning')}
          />
          <SummaryBadge
            count={summary.bySeverity.info}
            label="Info"
            severity="info"
            selected={selectedSeverity === 'info'}
            onClick={() => setSelectedSeverity(selectedSeverity === 'info' ? 'all' : 'info')}
          />
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 px-4 py-2 border-b bg-white">
        <Filter className="w-4 h-4 text-gray-400" />

        {/* Type filter */}
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value as ConflictType | 'all')}
          className="text-xs border rounded px-2 py-1 bg-white"
        >
          <option value="all">All Types</option>
          <option value="capacity_overload">Capacity Overload</option>
          <option value="late_delivery">Late Delivery</option>
          <option value="material_shortage">Material Shortage</option>
          <option value="resource_conflict">Resource Conflict</option>
          <option value="sequence_violation">Sequence Violation</option>
          <option value="employee_unavailable">Employee Unavailable</option>
          <option value="maintenance_conflict">Maintenance Conflict</option>
        </select>

        {/* Show resolved toggle */}
        <label className="flex items-center gap-1.5 text-xs text-gray-600 ml-auto cursor-pointer">
          <input
            type="checkbox"
            checked={showResolved}
            onChange={(e) => setShowResolved(e.target.checked)}
            className="rounded border-gray-300"
          />
          Show Resolved
        </label>
      </div>

      {/* Conflict list */}
      <div className="flex-1 overflow-y-auto">
        {filteredConflicts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-6 text-gray-500">
            <Check className="w-10 h-10 mb-3 text-green-400" />
            <p className="text-sm">
              {conflicts.length === 0
                ? 'No Conflicts Found'
                : 'All Conflicts Resolved!'}
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredConflicts.map((conflict) => (
              <ConflictItem
                key={conflict.id}
                conflict={conflict}
                isExpanded={expandedId === conflict.id}
                onToggle={() => setExpandedId(expandedId === conflict.id ? null : conflict.id)}
                resolutionNote={resolutionNote}
                onNoteChange={setResolutionNote}
                onResolve={() => handleResolve(conflict.id)}
                canResolve={!!onResolve && !conflict.is_resolved}
                isResolving={isResolving}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Summary badge component
interface SummaryBadgeProps {
  count: number;
  label: string;
  severity: ConflictSeverity;
  selected: boolean;
  onClick: () => void;
}

function SummaryBadge({ count, label, severity, selected, onClick }: SummaryBadgeProps) {
  const colors = getSeverityColor(severity);
  const Icon = SeverityIcons[severity];

  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs transition-all',
        selected ? `${colors.bg} ${colors.text} ring-2 ring-offset-1` : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
        severity === 'error' && selected && 'ring-red-300',
        severity === 'warning' && selected && 'ring-amber-300',
        severity === 'info' && selected && 'ring-blue-300'
      )}
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="font-medium">{count}</span>
      <span>{label}</span>
    </button>
  );
}

// Conflict item component
interface ConflictItemProps {
  conflict: PlanningConflict;
  isExpanded: boolean;
  onToggle: () => void;
  resolutionNote: string;
  onNoteChange: (note: string) => void;
  onResolve: () => void;
  canResolve: boolean;
  isResolving?: boolean;
}

function ConflictItem({
  conflict,
  isExpanded,
  onToggle,
  resolutionNote,
  onNoteChange,
  onResolve,
  canResolve,
  isResolving,
}: ConflictItemProps) {
  const colors = getSeverityColor(conflict.severity);
  const SeverityIcon = SeverityIcons[conflict.severity];
  const TypeIcon = ConflictTypeIcons[conflict.conflict_type] || AlertTriangle;

  return (
    <div className={clsx('transition-colors', conflict.is_resolved && 'opacity-60')}>
      {/* Main row */}
      <div
        className={clsx(
          'flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50',
          isExpanded && 'bg-gray-50'
        )}
        onClick={onToggle}
      >
        {/* Severity icon */}
        <div className={clsx('mt-0.5', colors.icon)}>
          <SeverityIcon className="w-5 h-5" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={clsx(
                'px-2 py-0.5 text-xs rounded font-medium',
                colors.bg,
                colors.text
              )}
            >
              {conflict.conflict_type_display}
            </span>
            {conflict.is_resolved && (
              <span className="flex items-center gap-1 text-xs text-green-600">
                <Check className="w-3 h-3" />
                Resolved
              </span>
            )}
          </div>

          <p className="text-sm text-gray-800 line-clamp-2">{conflict.message}</p>

          {/* Metadata */}
          <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
            {conflict.affected_work_center_code && (
              <span className="flex items-center gap-1">
                <TypeIcon className="w-3.5 h-3.5" />
                {conflict.affected_work_center_code}
              </span>
            )}
            {conflict.affected_date && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {new Date(conflict.affected_date).toLocaleDateString('en-GB')}
              </span>
            )}
          </div>
        </div>

        {/* Expand icon */}
        <div className="text-gray-400">
          {isExpanded ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="px-4 pb-4 pl-12 bg-gray-50">
          {/* Full message */}
          <div className="mb-3 p-3 bg-white rounded border text-sm text-gray-700">
            {conflict.message}
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-2 gap-2 text-xs mb-3">
            <div>
              <span className="text-gray-500">Created:</span>
              <span className="ml-2 text-gray-700">
                {new Date(conflict.created_at).toLocaleString('en-GB')}
              </span>
            </div>
            {conflict.resolved_at && (
              <div>
                <span className="text-gray-500">Resolved:</span>
                <span className="ml-2 text-gray-700">
                  {new Date(conflict.resolved_at).toLocaleString('en-GB')}
                </span>
              </div>
            )}
            {conflict.resolved_by && (
              <div>
                <span className="text-gray-500">Resolved By:</span>
                <span className="ml-2 text-gray-700">{conflict.resolved_by}</span>
              </div>
            )}
          </div>

          {/* Resolution section */}
          {conflict.is_resolved ? (
            conflict.resolution_note && (
              <div className="p-2 bg-green-50 rounded border border-green-200">
                <div className="text-xs text-green-600 font-medium mb-1">Resolution Note:</div>
                <div className="text-sm text-green-800">{conflict.resolution_note}</div>
              </div>
            )
          ) : canResolve && (
            <div className="space-y-2">
              <textarea
                value={resolutionNote}
                onChange={(e) => onNoteChange(e.target.value)}
                placeholder="Enter resolution note..."
                className="w-full px-3 py-2 text-sm border rounded resize-none focus:ring-2 focus:ring-[#45bdd6] focus:border-transparent"
                rows={2}
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onNoteChange('');
                  }}
                  className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onResolve();
                  }}
                  disabled={!resolutionNote.trim() || isResolving}
                  className={clsx(
                    'px-3 py-1.5 text-xs text-white rounded transition-colors',
                    resolutionNote.trim() && !isResolving
                      ? 'bg-green-500 hover:bg-green-600'
                      : 'bg-gray-300 cursor-not-allowed'
                  )}
                >
                  {isResolving ? (
                    <span className="flex items-center gap-1">
                      <RefreshCw className="w-3 h-3 animate-spin" />
                      Saving...
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Mark as Resolved
                    </span>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ConflictListWidget;
